import { request } from '@/utils/http'
import type { QueryCondition, SortCondition, PageRequest } from '@/types/api'

// 接口类型定义
export interface ContractListItem {
  id: string
  contractNumber: string
  carrierId: string // API返回字符串格式
  carrierDisplay: string | null
  carrierCode: string | null
  carrierNameCn: string | null
  pricingNature: number
  pricingNatureName: string
  routeDisplay: string
  routeDisplayEn?: string
  routeNames: string[]
  routeNamesEn?: string[]
  applicableCommodity: number | null
  commodityCargoTypeId?: string | null
  nacDisplay: string
  nacCodes: string[]
  mqc: number | null
  spaceGuarantee: number | null
  spaceGuaranteeName: string
  spaceGuaranteeTeu?: number | null
  effectivePeriod: string
  effectiveDate: string
  expiryDate: string
  enabled: number
  enabledName: string
}

export interface ContractDetail {
  id: string
  contractNumber: string
  carrierId: number
  carrierCode: string | null
  carrierNameCn: string | null
  carrierNameEn: string | null
  pricingNature: number
  pricingNatureName: string
  routeIds: number[]
  routeNames: string[]
  routeNamesEn?: string[]
  routeDisplayEn?: string
  applicableCommodity: number | null
  commodityCargoTypeId?: string | null
  mqc: number | null
  spaceGuarantee: number | null
  spaceGuaranteeName: string
  spaceGuaranteeTeu?: number | null
  effectiveDate: string
  expiryDate: string
  effectivePeriod: string
  nacCodes: string[]
  enabled: number
  enabledName: string
  version: number
}

export interface ContractPageRequest extends PageRequest {
  // 传统查询字段（保持向后兼容）
  keyword?: string
  carrierId?: number
  pricingNature?: number
  enabled?: number
  // 高级查询功能通过继承 PageRequest 的 conditions 和 sorts 字段实现
}

export interface ContractSaveRequest {
  contractNumber: string
  carrierId: number
  pricingNature: number
  routeIds: number[]
  routeNames: string[]
  /** 适用品名旧枚举，仅兼容旧客户端；新客户端使用 commodityCargoTypeId */
  applicableCommodity?: number | null
  /** 适用品名（dmn_cargo_type.id） */
  commodityCargoTypeId?: string | null
  /** MQC最小起订量，非必填 */
  mqc?: number | null
  /** 舱保数值（TEU/月）；空-未填写，0-无，>0-有X TEU/月 */
  spaceGuaranteeTeu?: number | null
  effectiveDate: string
  expiryDate: string
  nacCodes: string[]
  enabled: number
}

export interface ContractUpdateRequest extends ContractSaveRequest {
  id: string
  version: number
  /** 适用品名旧枚举，仅兼容旧客户端 */
  applicableCommodity?: number | null
  /** 适用品名（dmn_cargo_type.id） */
  commodityCargoTypeId?: string | null
  /** MQC最小起订量，非必填 */
  mqc?: number | null
  /** 舱保旧枚举，仅兼容旧客户端 */
  spaceGuarantee?: number | null
  /** 舱保数值（TEU/月）；空-未填写，0-无，>0-有X TEU/月 */
  spaceGuaranteeTeu?: number | null
}

export interface BatchUpdateStatusRequest {
  ids: string[]
  enabled: number
}

export interface BaseOptions {
  pricingNatureOptions: Array<{ value: number; label: string }>
  commodityOptions: Array<{ value: number; label: string }>
  spaceGuaranteeOptions: Array<{ value: number; label: string }>
  carrierOptions: Array<{ value: number; label: string; code: string }>
  routeOptions: Array<{ value: number; label: string; code: string }>
}

// API 接口
export const contractApi = {
  // 分页查询合约列表
  page: (params: ContractPageRequest) => {
    return request.post<{
      records: ContractListItem[]
      total: number
      current: number
      size: number
    }>('/price/contract/page', params)
  },

  // 查询合约列表
  list: (params: ContractPageRequest) => {
    return request.post<ContractListItem[]>('/price/contract/list', params)
  },

  // 获取合约详情
  get: (id: string) => {
    return request.post<ContractDetail>('/price/contract/get', { id })
  },

  // 新增合约
  save: (params: ContractSaveRequest) => {
    return request.post<boolean>('/price/contract/save', params)
  },

  // 更新合约
  update: (params: ContractUpdateRequest) => {
    return request.post<boolean>('/price/contract/update', params)
  },

  // 删除合约
  delete: (id: string) => {
    return request.post<boolean>('/price/contract/delete', { id })
  },

  // 批量更新状态
  batchUpdateStatus: (params: BatchUpdateStatusRequest) => {
    const { ids, enabled } = params
    const endpoint = enabled === 1 ? '/price/contract/batch-enable' : '/price/contract/batch-disable'
    return request.post<boolean>(endpoint, params)
  },

  // 根据船公司ID查询合约列表
  listByCarrier: (carrierId: string) => {
    return request.post<ContractListItem[]>('/price/contract/list-by-carrier', { id: carrierId })
  },

  // 根据合约编号集合查询合约列表
  listByContractNumbers: (contractNumbers: string[]) => {
    return request.post<ContractListItem[]>('/price/contract/listByContractNumbers', { contractNumbers })
  },

  // 校验合约编号是否存在
  checkContractNumber: (contractNumber: string, excludeId?: string) => {
    return request.post<boolean>('/price/contract/check-contract-number', {
      contractNumber,
      excludeId
    })
  }
}

export default contractApi